import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../services/api';

const BusinessExpenses = ({ projectMode = false }) => {
  // Contexte du projet si en mode projet
  const outletContext = useOutletContext();
  const projectId = projectMode ? outletContext?.projectId : null;
  const canManage = projectMode ? outletContext?.canManage : true;
  
  // Base URL de l'API selon le mode
  const apiBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/business';
  
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  // Default categories if none exist
  const defaultCategories = [
    { nom: 'Fournitures', icon: '📦' },
    { nom: 'Transport', icon: '🚗' },
    { nom: 'Loyer', icon: '🏠' },
    { nom: 'Électricité', icon: '⚡' },
    { nom: 'Téléphone/Internet', icon: '📱' },
    { nom: 'Marketing', icon: '📢' },
    { nom: 'Salaires', icon: '👥' },
    { nom: 'Autres', icon: '📋' }
  ];

  useEffect(() => {
    loadExpenses();
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, projectId]);

  const loadExpenses = async () => {
    try {
      let url = `${apiBase}/expenses`;
      if (filter !== 'all') url += `?category=${filter}`;
      const res = await api.get(url);
      setExpenses(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await api.get(`${apiBase}/expense-categories`);
      const cats = Array.isArray(res.data) ? res.data : [];
      setCategories(cats.length > 0 ? cats : defaultCategories);
    } catch (err) {
      setCategories(defaultCategories);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    try {
      await api.delete(`${apiBase}/expenses/${id}`);
      loadExpenses();
    } catch (err) {
      console.error(err);
    }
  };

  // Stats
  const now = new Date();
  const thisMonth = expenses.filter(e => {
    const d = new Date(e.date_depense);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  const stats = {
    totalMonth: thisMonth.reduce((sum, e) => sum + parseFloat(e.montant || 0), 0),
    countMonth: thisMonth.length,
    totalAll: expenses.reduce((sum, e) => sum + parseFloat(e.montant || 0), 0),
    byCategory: categories.map(cat => ({
      ...cat,
      total: expenses.filter(e => e.categorie === cat.nom).reduce((sum, e) => sum + parseFloat(e.montant || 0), 0)
    })).sort((a, b) => b.total - a.total)
  };

  if (loading) {
    return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div></div>;
  }

  // Filtrage par recherche
  const filteredExpenses = expenses.filter(expense => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      expense.description?.toLowerCase().includes(searchLower) ||
      expense.categorie?.toLowerCase().includes(searchLower) ||
      expense.fournisseur?.toLowerCase().includes(searchLower) ||
      expense.reference?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedExpenses = filteredExpenses.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">Dépenses</h1>
          <p className="text-gray-500 text-sm">Suivez vos dépenses professionnelles</p>
        </div>
        <div className="flex gap-2">
          {canManage && (
            <button
              onClick={() => setShowCategoryModal(true)}
              className="px-3 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition text-sm"
            >
              ⚙️ <span className="hidden sm:inline">Catégories</span>
            </button>
          )}
          {canManage && (
            <button
              onClick={() => { setEditingExpense(null); setShowModal(true); }}
              className="px-3 md:px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition flex items-center gap-2 text-sm"
            >
              <span>➕</span> <span className="hidden sm:inline">Nouvelle</span> dépense
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-red-500 text-xl md:text-2xl mb-1 md:mb-2">📅</div>
          <div className="text-lg md:text-xl font-bold text-red-600">{stats.totalMonth.toLocaleString()} <span className="text-xs">XOF</span></div>
          <div className="text-xs md:text-sm text-gray-500">Ce mois ({stats.countMonth})</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-gray-500 text-xl md:text-2xl mb-1 md:mb-2">📊</div>
          <div className="text-lg md:text-xl font-bold text-gray-800">{stats.totalAll.toLocaleString()} <span className="text-xs">XOF</span></div>
          <div className="text-xs md:text-sm text-gray-500">Total ({expenses.length})</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4 col-span-2 sm:col-span-1">
          <div className="text-amber-500 text-xl md:text-2xl mb-1 md:mb-2">🏆</div>
          <div className="text-sm font-bold text-gray-800">
            {stats.byCategory[0]?.nom || 'N/A'}
          </div>
          <div className="text-xs md:text-sm text-gray-500">
            Top: {stats.byCategory[0]?.total?.toLocaleString() || 0} XOF
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="bg-white rounded-xl shadow p-4 mb-6">
        <h3 className="font-semibold text-gray-800 mb-3">Par catégorie</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {stats.byCategory.slice(0, 8).map((cat, index) => (
            <button
              key={index}
              onClick={() => setFilter(filter === cat.nom ? 'all' : cat.nom)}
              className={`p-3 rounded-xl text-left transition ${
                filter === cat.nom
                  ? 'bg-red-100 border-2 border-red-500'
                  : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
              }`}
            >
              <div className="text-lg mb-1">{cat.icon}</div>
              <div className="text-sm font-medium text-gray-800">{cat.nom}</div>
              <div className="text-xs text-gray-500">{cat.total.toLocaleString()} XOF</div>
            </button>
          ))}
        </div>
        {filter !== 'all' && (
          <button
            onClick={() => setFilter('all')}
            className="mt-3 text-sm text-indigo-600 hover:underline"
          >
            ✕ Effacer le filtre
          </button>
        )}
      </div>

      {/* Barre de recherche */}
      <div className="bg-white rounded-xl shadow p-4 mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Rechercher par description, catégorie, fournisseur..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Résultat de recherche */}
      {search && (
        <div className="mb-4 text-sm text-gray-500">
          {filteredExpenses.length} résultat(s) pour "{search}"
        </div>
      )}

      {/* Expenses List */}
      {paginatedExpenses.length > 0 ? (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Catégorie</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Description</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Fournisseur</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Montant</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {paginatedExpenses.map(expense => (
                <tr key={expense.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {new Date(expense.date_depense).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-sm">
                      {categories.find(c => c.nom === expense.categorie)?.icon || '📋'} {expense.categorie}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{expense.description || '-'}</div>
                    {expense.reference && (
                      <div className="text-xs text-gray-400">Réf: {expense.reference}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {expense.fournisseur || '-'}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-red-600">
                    -{parseFloat(expense.montant).toLocaleString()} XOF
                  </td>
                  <td className="px-4 py-3">
                    {canManage ? (
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => { setEditingExpense(expense); setShowModal(true); }}
                          className="p-2 bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100 transition"
                          title="Modifier"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(expense.id)}
                          className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">Lecture seule</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Affichage {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredExpenses.length)} sur {filteredExpenses.length}
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ««
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  «
                </button>
                {(() => {
                  const pages = [];
                  const showPages = new Set();
                  showPages.add(1);
                  showPages.add(totalPages);
                  for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
                    showPages.add(i);
                  }
                  const sortedPages = [...showPages].sort((a, b) => a - b);
                  sortedPages.forEach((page, idx) => {
                    if (idx > 0 && page - sortedPages[idx - 1] > 1) {
                      pages.push(<span key={`ellipsis-${page}`} className="px-2 text-gray-400">...</span>);
                    }
                    pages.push(
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded ${currentPage === page ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                      >
                        {page}
                      </button>
                    );
                  });
                  return pages;
                })()}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  »
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  »»
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <span className="text-6xl mb-4 block">💸</span>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {search ? 'Aucun résultat' : 'Aucune dépense'}
          </h3>
          <p className="text-gray-500 mb-4">
            {search ? 'Essayez une autre recherche' : 'Enregistrez votre première dépense'}
          </p>
          {!search && (
            <button
              onClick={() => { setEditingExpense(null); setShowModal(true); }}
              className="px-6 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition"
            >
              Nouvelle dépense
            </button>
          )}
        </div>
      )}

      {/* Expense Modal */}
      {showModal && (
        <ExpenseModal
          expense={editingExpense}
          categories={categories}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            loadExpenses();
          }}
          apiBase={apiBase}
        />
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <CategoryModal
          categories={categories}
          onClose={() => setShowCategoryModal(false)}
          onSuccess={() => {
            setShowCategoryModal(false);
            loadCategories();
          }}
          apiBase={apiBase}
        />
      )}
    </div>
  );
};

// Expense Modal Component
const ExpenseModal = ({ expense, categories, onClose, onSuccess, apiBase = '/business' }) => {
  const [form, setForm] = useState({
    categorie: expense?.categorie || categories[0]?.nom || '',
    description: expense?.description || '',
    montant: expense?.montant || '',
    fournisseur: expense?.fournisseur || '',
    reference: expense?.reference || '',
    date_depense: expense?.date_depense?.split(' ')[0] || new Date().toISOString().split('T')[0],
    notes: expense?.notes || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (expense) {
        await api.put(`${apiBase}/expenses/${expense.id}`, form);
      } else {
        await api.post(`${apiBase}/expenses`, form);
      }
      onSuccess();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">
            {expense ? 'Modifier la dépense' : 'Nouvelle dépense'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
            <select
              value={form.categorie}
              onChange={(e) => setForm({ ...form, categorie: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              required
            >
              {categories.map((cat, index) => (
                <option key={index} value={cat.nom}>{cat.icon} {cat.nom}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              placeholder="Achat de..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Montant *</label>
            <input
              type="number"
              min="0"
              value={form.montant}
              onChange={(e) => setForm({ ...form, montant: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              placeholder="0"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fournisseur</label>
              <input
                type="text"
                value={form.fournisseur}
                onChange={(e) => setForm({ ...form, fournisseur: e.target.value })}
                className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Référence</label>
              <input
                type="text"
                value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={form.date_depense}
              onChange={(e) => setForm({ ...form, date_depense: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows="2"
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : expense ? 'Modifier' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Category Modal Component
const CategoryModal = ({ categories, onClose, onSuccess, apiBase = '/business' }) => {
  const [newCat, setNewCat] = useState({ nom: '', icon: '📋' });
  const [saving, setSaving] = useState(false);

  const icons = ['📦', '🚗', '🏠', '⚡', '📱', '📢', '👥', '🍔', '🛠️', '📋', '💊', '🎓', '✈️', '🖥️', '📝'];

  const handleAdd = async () => {
    if (!newCat.nom) return;
    setSaving(true);
    try {
      await api.post(`${apiBase}/expense-categories`, newCat);
      setNewCat({ nom: '', icon: '📋' });
      onSuccess();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">Catégories de dépenses</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <div className="space-y-4">
          {/* Existing categories */}
          <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
            {categories.map((cat, index) => (
              <div key={index} className="p-2 bg-gray-50 rounded-lg flex items-center gap-2">
                <span>{cat.icon}</span>
                <span className="text-sm">{cat.nom}</span>
              </div>
            ))}
          </div>

          {/* Add new */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-700 mb-2">Ajouter une catégorie</h4>
            <div className="flex gap-2">
              <select
                value={newCat.icon}
                onChange={(e) => setNewCat({ ...newCat, icon: e.target.value })}
                className="border rounded-lg px-2 py-2"
              >
                {icons.map(icon => (
                  <option key={icon} value={icon}>{icon}</option>
                ))}
              </select>
              <input
                type="text"
                value={newCat.nom}
                onChange={(e) => setNewCat({ ...newCat, nom: e.target.value })}
                placeholder="Nom"
                className="flex-1 border rounded-lg px-3 py-2"
              />
              <button
                onClick={handleAdd}
                disabled={saving || !newCat.nom}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                +
              </button>
            </div>
          </div>
        </div>

        <button
          onClick={onClose}
          className="mt-4 w-full py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200"
        >
          Fermer
        </button>
      </div>
    </div>
  );
};

export default BusinessExpenses;
